import { RecipeV2Detail } from '../types';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Builds and opens a standalone, printable "recipe instruction card" (Gousto/HelloFresh style)
 * for a recipe - servings/yield, ingredients list and numbered method steps - in a new browser
 * tab, then triggers the browser print dialog.
 *
 * lazy: renders inline HTML/CSS into a new window and calls window.print() rather than
 * generating a downloadable PDF file. Ceiling: no "Save as PDF" without the user picking that
 * destination in the print dialog, and no server-stored export artifact. Upgrade path: swap
 * the new-window HTML for a jsPDF (already an installed dependency) render if a true downloadable
 * PDF file is required later.
 */
export function openRecipePrintCard(recipe: RecipeV2Detail): void {
  const printWindow = window.open('', '_blank', 'width=850,height=1100');
  if (!printWindow) return;

  const allergenNames = recipe.allergenSummary.map((a) => a.allergenName).filter(Boolean);

  const ingredientsHtml = recipe.components
    .map(
      (c) => `<li><span class="qty">${c.quantity} ${escapeHtml(c.uom)}</span> ${escapeHtml(c.componentDescription)}</li>`,
    )
    .join('');

  const stepsHtml = (recipe.steps ?? [])
    .slice()
    .sort((a, b) => a.stepNumber - b.stepNumber)
    .map((step, index) => {
      const meta = [
        step.durationMinutes != null ? `${step.durationMinutes} min` : null,
        step.temperature ? step.temperature : null,
        step.equipment.length > 0 ? step.equipment.map((e) => e.name).join(', ') : null,
      ]
        .filter(Boolean)
        .join(' &middot; ');

      return `
        <li class="step">
          <span class="step-number">${index + 1}</span>
          <div class="step-body">
            <h3>${escapeHtml(step.title || `Step ${index + 1}`)}</h3>
            <p>${escapeHtml(step.instruction)}</p>
            ${meta ? `<p class="step-meta">${meta}</p>` : ''}
          </div>
        </li>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(recipe.description)} - Recipe Card</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 0; padding: 32px; }
  .card { max-width: 760px; margin: 0 auto; }
  h1 { font-size: 26px; margin: 0 0 4px; }
  .subtitle { color: #555; font-size: 13px; margin: 0 0 16px; }
  .meta-row { display: flex; gap: 24px; padding: 12px 0; border-top: 2px solid #1a1a1a; border-bottom: 2px solid #1a1a1a; margin-bottom: 20px; }
  .meta-row div { font-size: 13px; }
  .meta-row strong { display: block; font-size: 15px; }
  .allergens { font-size: 12px; color: #7a1f1f; margin-bottom: 20px; }
  h2 { font-size: 16px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #ccc; padding-bottom: 6px; }
  ul.ingredients { list-style: none; padding: 0; margin: 0 0 24px; column-count: 2; column-gap: 32px; }
  ul.ingredients li { font-size: 13px; padding: 4px 0; break-inside: avoid; }
  ul.ingredients .qty { font-weight: 700; margin-right: 6px; }
  ol.steps, ul.steps { list-style: none; padding: 0; margin: 0; }
  li.step { display: flex; gap: 14px; margin-bottom: 18px; break-inside: avoid; }
  .step-number { flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%; background: #1a1a1a; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; }
  .step-body h3 { margin: 0 0 4px; font-size: 14px; }
  .step-body p { margin: 0; font-size: 13px; line-height: 1.4; }
  .step-meta { color: #555; font-style: italic; margin-top: 4px !important; }
  @media print {
    body { padding: 0; }
  }
</style>
</head>
<body>
  <div class="card">
    <h1>${escapeHtml(recipe.description)}</h1>
    <p class="subtitle">${escapeHtml(recipe.itemNumber)} &middot; ${escapeHtml(recipe.primaryCategory)}</p>
    <div class="meta-row">
      <div><strong>${recipe.yieldQuantity} ${escapeHtml(recipe.yieldUom)}</strong>Yield</div>
      ${recipe.productionMethod ? `<div><strong>${escapeHtml(recipe.productionMethod.name)}</strong>Production Method</div>` : ''}
    </div>
    ${allergenNames.length > 0 ? `<p class="allergens">Allergens: ${escapeHtml(allergenNames.join(', '))}</p>` : ''}
    <h2>Ingredients</h2>
    <ul class="ingredients">${ingredientsHtml}</ul>
    <h2>Method</h2>
    <ol class="steps">${stepsHtml}</ol>
  </div>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
  };
}
